/**
 * Shared terrain mesh geometry — UV vertex buffer + uint32 index buffer for a
 * `terrainGridResolution × terrainGridResolution` flat grid. Both the main terrain
 * render pass and the shadow pass consume the same geometry, so it's built once
 * in frame.ts and handed to both rather than each subsystem allocating ~1.5 MB
 * of identical buffers.
 *
 * Vertex stride: vec2<f32> UV (8 bytes); winding: CCW from above.
 */

import { config } from '../config.ts';

export interface TerrainGeometry {
  readonly vertexBuffer: GPUBuffer;
  readonly indexBuffer: GPUBuffer;
  readonly indexCount: number;
  readonly indexFormat: 'uint32';
  destroy(): void;
}

export function createTerrainGeometry(device: GPUDevice): TerrainGeometry {
  const grid = config.terrainGridResolution;
  const vertCount = grid * grid;
  const quadCount = (grid - 1) * (grid - 1);
  const indexCount = quadCount * 6;

  const vertexData = new Float32Array(vertCount * 2);
  {
    const inv = 1 / (grid - 1);
    let i = 0;
    for (let z = 0; z < grid; z++) {
      const v = z * inv;
      for (let x = 0; x < grid; x++) {
        vertexData[i++] = x * inv;
        vertexData[i++] = v;
      }
    }
  }
  const vertexBuffer = device.createBuffer({
    label: 'terrain.shared.vertex',
    size: vertexData.byteLength,
    usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
    mappedAtCreation: true,
  });
  new Float32Array(vertexBuffer.getMappedRange()).set(vertexData);
  vertexBuffer.unmap();

  const indexBuffer = device.createBuffer({
    label: 'terrain.shared.index',
    size: indexCount * 4,
    usage: GPUBufferUsage.INDEX | GPUBufferUsage.COPY_DST,
    mappedAtCreation: true,
  });
  {
    const indices = new Uint32Array(indexBuffer.getMappedRange());
    let i = 0;
    for (let z = 0; z < grid - 1; z++) {
      for (let x = 0; x < grid - 1; x++) {
        const tl = z * grid + x;
        const tr = tl + 1;
        const bl = tl + grid;
        const br = bl + 1;
        indices[i++] = tl;
        indices[i++] = bl;
        indices[i++] = br;
        indices[i++] = tl;
        indices[i++] = br;
        indices[i++] = tr;
      }
    }
    indexBuffer.unmap();
  }

  return {
    vertexBuffer,
    indexBuffer,
    indexCount,
    indexFormat: 'uint32',
    destroy() {
      vertexBuffer.destroy();
      indexBuffer.destroy();
    },
  };
}
